import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HistoryBaseComponent } from 'src/app/history-base/history-base.component';
import { Config } from 'src/infrastructure/config';
import { CartLine } from 'src/models/DTO/cart/cartLine';
import { WholeCart } from 'src/models/DTO/cart/wholeCart';
import { UserDTO } from 'src/models/DTO/user/user';
import { UpdateCartLineAmountModel } from 'src/models/requestModels/cart/updateCartLineAmountModel';
import { LocalstorageService } from 'src/services/core/localstorage/localstorage.service';
import { ToastService } from 'src/services/core/toast/toast.service';
import { CartService } from 'src/services/request/cart/cart.service';
import { ImagesService } from 'src/services/request/images/images.service';
import { HistoryService } from 'src/services/state/history/history.service';
import { LoggedUserService } from 'src/services/state/loggeduser/loggeduser.service';
import { OrederStateService } from 'src/services/state/order/oreder-state.service';

declare var $: any;
declare var bootstrap: any;

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.css']
})
export class CartComponent extends HistoryBaseComponent implements OnInit {
  private _cart?: WholeCart;
  private _cartLinePerOrganisation: { [key: string]: CartLine[] } = {};
  private _cartTotalPerOrganisation: { [key: string]: number } = {};
  private _cartSelectedPerOrganisation: { [key: string]: boolean } = {};
  private _cartLines: { [key: string]: CartLine } = {};
  private bsOffcanvas: any;
  private _loggedUser?: UserDTO;

  public cartURL: string = 'bottomtabs/cart';

  constructor(
    private cartService: CartService,
    private imageService: ImagesService,
    private orderStateService: OrederStateService,
    private router: Router,
    private userStateService: LoggedUserService,
    protected location: Location,
    private toastService: ToastService,
    private localStorage: LocalstorageService,
    protected historyService: HistoryService
  ) {
    super(location, historyService);

    this.userStateService.userInfo$.subscribe((user) => {
      this._loggedUser = user;

      if (this._loggedUser?.data?.cart) {
        this.loadCart();
      }
    });
  }

  ngOnInit(): void {}
  private loadCart(): void {
    this._cart = this._loggedUser!.data.cart;

    this._cart!.lines.forEach((line) => {
      this._cartLines[line.uuid] = line;

      this.imageService.getImagesForProduct('SKU', line.sku.uuid).subscribe((image) => {
        if (image.data.length) {
          line.imageSource = image.data[0]?.original.url || '/assets/images/shared/logo.png';
        } else {
          this.imageService.getImagesForProduct('PRODUCT', line.sku.product.uuid).subscribe((productImage) => {
            line.imageSource = productImage.data[0]?.original.url || '/assets/images/shared/logo.png';
          });
        }
      });
    });

    this.filterCartLines();
  }

  private filterCartLines(): void {
    this._cartTotalPerOrganisation = {};
    this._cartLinePerOrganisation = {};

    this._cart?.lines.forEach((line) => {
      line.sku.options.forEach((option) => {
        option.valueDecoded = option.option.values.find((allValues) => allValues.uuid == option.value)?.value;
      });

      line.sku.options.forEach((option) => {
        if (!option.valueDecoded) {
          option.valueDecoded = option.value;
        }
      });

      const organisationUuid = line.sku.product.organisation.uuid;
      const lineTotal = Number.parseFloat(this.getPriceForCartLinePerAmount(line)) * line.quantity;

      if (this._cartLinePerOrganisation[organisationUuid]) {
        this._cartLinePerOrganisation[organisationUuid].push(line);
        this._cartTotalPerOrganisation[organisationUuid] += lineTotal;
      } else {
        this._cartLinePerOrganisation[organisationUuid] = [line];
        this._cartTotalPerOrganisation[organisationUuid] = lineTotal;
      }
    });

    this.selectAll();
  }

  private selectAll(): void {
    this.toggle(true);
    this.recalculateSelected();
  }

  private recalculateSelected(): void {
    Object.keys(this._cartLinePerOrganisation).forEach((orgKey) => {
      this._cartSelectedPerOrganisation[orgKey] = true;

      this._cartLinePerOrganisation[orgKey].forEach((product) => {
        if (!product.selected) {
          this._cartSelectedPerOrganisation[orgKey] = false;
        }

        this._cart?.lines.forEach((line) => {
          if (line.uuid == product.uuid) {
            line.selected = product.selected;
          }
        });

        this._cartLines[product.uuid].selected = product.selected;
      });
    });
  }

  private getPriceForCartLinePerAmount(cartLine: CartLine): string {
    const pricing = cartLine.sku.product.pricing.sort(
      (pricingA, pricingB) => pricingA.quantity_from - pricingB.quantity_from
    );

    let priceToReturn = pricing[pricing.length - 1].price_per_piece;
    const productUUID = cartLine.sku.product.uuid;
    let quantity = 0;

    this.cart?.lines.forEach((line) => {
      if (line.sku.product.uuid == productUUID) {
        quantity += line.quantity;
      }
    });

    for (const price of pricing) {
      if (price.quantity_from <= quantity && price.quantity_to >= quantity) {
        priceToReturn = price.price_per_piece;
      }
    }

    return priceToReturn;
  }

  get cart(): WholeCart | undefined {
    return this._cart;
  }

  get cartLinePerOrganisation(): { [key: string]: CartLine[] } {
    return this._cartLinePerOrganisation;
  }

  get cartTotalPerOrganisation(): { [key: string]: number } {
    return this._cartTotalPerOrganisation;
  }

  get cartSelectedPerOrganisation(): { [key: string]: boolean } {
    return this._cartSelectedPerOrganisation;
  }

  get cartLines(): { [key: string]: CartLine } {
    return this._cartLines;
  }

  get numberOfCartLines(): number {
    return this.cart?.lines.length || 0;
  }

  get isAllSelected(): boolean {
    let isSelected = true;

    Object.keys(this._cartLinePerOrganisation).forEach((orgKey) => {
      this._cartLinePerOrganisation[orgKey].forEach((product) => {
        if (!product.selected) {
          isSelected = false;
        }
      });
    });

    return isSelected;
  }

  get allOrgs(): string[] {
    return Object.keys(this._cartTotalPerOrganisation);
  }

  get totalSelected(): string {
    let value = 0;

    this.cart?.lines.forEach((line) => {
      if (line.selected) {
        value += Number.parseFloat(this.getPriceForCartLinePerAmount(line)) * line.quantity;
      }
    });

    return value.toFixed(2);
  }

  get totalSelectedBase(): number {
    return Number(this.totalSelected) || 0;
  }

  get currentLink(): string {
    return this.localStorage.getLocalStorageItem(Config.bottomTapPageKey) || 'bottomtabs/home';
  }

  get screenStyle(): string {
    return 'position: absolute; top: 95px; overflow-y: scroll;';
  }

  getUnitPriceBase(cartLine: CartLine): number {
    const raw = this.getPriceForCartLinePerAmount(cartLine);
    return Number(raw) || 0;
  }

  getLineTotalBase(cartLine: CartLine): number {
    return this.getUnitPriceBase(cartLine) * (Number(cartLine.quantity) || 0);
  }

  pricingInfoText(cartLine: CartLine): string {
    const qty = Number(cartLine.quantity) || 0;
    return `${qty} x`;
  }

  getOrgTotalBase(orgKey: string): number {
    const total = this._cartTotalPerOrganisation?.[orgKey];
    return Number(total) || 0;
  }

  isProductAvailable(productPerOrg: CartLine): boolean {
    return !productPerOrg.sku.product.deleted_at && productPerOrg.sku.product.state == Config.activeState;
  }

  isIncreaseBtnDisabled(productPerOrg: CartLine): boolean {
    return productPerOrg.quantity == Number.parseInt(productPerOrg.sku.available_quantity, 10);
  }

  removeLine(productPerOrg: CartLine, orgs: string): void {
    this.cartService.deleteCartLine(this.cart!.uuid, productPerOrg.uuid).subscribe(() => {
      this._cartLinePerOrganisation[orgs] = this.cartLinePerOrganisation[orgs].filter(
        (product) => product.uuid != productPerOrg.uuid
      );

      this._cart!.lines = this._cart!.lines.filter((cartLine) => cartLine.uuid != productPerOrg.uuid);
      this._loggedUser!.data.cart!.lines = this._cart!.lines.filter((cartLine) => cartLine.uuid != productPerOrg.uuid);

      this.filterCartLines();
    });
  }

  goToProduct(productPerOrg: CartLine): void {
    this.router.navigate(['product/' + productPerOrg.sku.product.uuid + '/' + 'none']);
  }

  decreaseAmount(productPerOrg: CartLine, orgs: string): void {
    if (productPerOrg.quantity === 1) {
      return;
    }

    productPerOrg.quantity--;
    this.userStateService.addNewUser(this._loggedUser!);

    const cartLineUpdateRecord: UpdateCartLineAmountModel = {
      line_uuid: productPerOrg.uuid,
      new_quantity: productPerOrg.quantity
    };

    this.cartService.updateCartLine(this._cart!.uuid, cartLineUpdateRecord).subscribe(() => {
      this.filterCartLines();
    });
  }

  increaseAmount(productPerOrg: CartLine): void {
    if (Number.parseInt(productPerOrg.sku.available_quantity, 10) == productPerOrg.quantity) {
      return;
    }

    productPerOrg.quantity++;
    this.userStateService.addNewUser(this._loggedUser!);

    const cartLineUpdateRecord: UpdateCartLineAmountModel = {
      line_uuid: productPerOrg.uuid,
      new_quantity: productPerOrg.quantity
    };

    this.cartService.updateCartLine(this._cart!.uuid, cartLineUpdateRecord).subscribe(() => {
      this.filterCartLines();
    });
  }

  goToCategories(): void {
    this.router.navigate(['categories']);
  }

  toggle(value: boolean): void {
    Object.keys(this._cartLinePerOrganisation).forEach((orgKey) => {
      this._cartLinePerOrganisation[orgKey].forEach((product) => {
        product.selected = value;
      });
    });
  }

  pricingInfo(cartLine: CartLine): string {
    const price = this.getPriceForCartLinePerAmount(cartLine);
    const priceAsString = Number.parseFloat(price).toFixed(2);

    return cartLine.quantity + ' x ' + priceAsString + ' лв.';
  }

  getPriceAsFixedString(cartLine: CartLine): string[] | string {
    if (!cartLine.price_per_piece) {
      return '00.00';
    }

    const price = this.getPriceForCartLinePerAmount(cartLine);
    const priceCalculated = Number.parseFloat(price) * cartLine.quantity;
    const fixedPrice = priceCalculated.toFixed(2);
    const priceArray = fixedPrice.split('.');

    priceArray[0] = priceArray[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return priceArray;
  }

  getTotalPriceAsFixedString(price: string): string {
    if (!price) {
      return '00.00';
    }

    const fixedPrice = Number.parseFloat(price).toFixed(2);
    const priceArray = fixedPrice.split('.');

    priceArray[0] = priceArray[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return priceArray[0] + '.' + priceArray[1];
  }

  continue(): void {
    console.log(this._cart);
  }

  allToggle(event: any): void {
    this.toggle(event.target.checked);
    this.recalculateSelected();
  }

  orgToggle(event: any, orgUUID: string): void {
    this._cartSelectedPerOrganisation[orgUUID] = event.target.checked;

    this._cartLinePerOrganisation[orgUUID].forEach((product) => {
      product.selected = event.target.checked;
    });

    this.recalculateSelected();
  }

  productToggle(event: any, productUUId: string): void {
    Object.keys(this._cartLinePerOrganisation).forEach((orgKey) => {
      this._cartLinePerOrganisation[orgKey].forEach((product) => {
        if (product.uuid == productUUId) {
          product.selected = event.target.checked;
        }
      });
    });

    this._cartLines[productUUId].selected = event.target.checked;
    this.recalculateSelected();
  }

  createOrder(): void {
    for (const line of this._cart!.lines) {
      if (line.selected && !this.isProductAvailable(line)) {
        this.toastService.warning('Моля премахнете изчерпаните продукти');
        return;
      }
    }

    this._cart!.lines.forEach((line) => {
      if (!line.selected) {
        this.cartService.deleteCartLine(this._cart!.uuid, line.uuid);
      }
    });

    this.orderStateService.addNewOrderInfo({
      isNewAdrres: false,
      isNewPaymentMethod: false,
      isNewInvoiceAddress: false,
      toCarrierOffice: false,
      toAddress: true,
      personalInvoice: false,
      companyInvoice: false,
      cardPayment: true,
      cashPayment: false,
      checkedCartLines: this._cart!.lines.filter((line) => line.selected),
      cart: this._cart
    });

    this.router.navigate(['order/details']);
  }
}
